import { useEffect, useState, useCallback } from "react";
import { api, currency, dateShort } from "../lib/api";
import { PageHeader } from "../components/Bits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CheckCircle2, ChevronLeft, ChevronRight, Package, Palette, Truck, X } from "lucide-react";
import { toast } from "sonner";

function Thumb({ src, alt }) {
  if (!src) {
    return (
      <div className="h-14 w-14 shrink-0 rounded-lg bg-slate-100 border border-slate-200 grid place-items-center">
        <Package className="h-5 w-5 text-slate-400" />
      </div>
    );
  }
  return (
    <img src={src} alt={alt || ""} loading="lazy"
      className="h-14 w-14 shrink-0 rounded-lg object-cover border border-slate-200" />
  );
}

export default function ProductionBoard() {
  const [orders, setOrders] = useState([]);
  const [stages, setStages] = useState([]);
  const [presets, setPresets] = useState([]);
  const [paintOrder, setPaintOrder] = useState(null);
  const [paintHex, setPaintHex] = useState("#000000");
  const [paintLabel, setPaintLabel] = useState("");
  const [trackOrder, setTrackOrder] = useState(null);
  const [trackCode, setTrackCode] = useState("");
  const [trackCarrier, setTrackCarrier] = useState("");

  const load = useCallback(async () => {
    const [o, s, p] = await Promise.all([
      api.get("/orders", { params: { status: "active" } }),
      api.get("/stages"),
      api.get("/paint-colors"),
    ]);
    setOrders(o.data); setStages(s.data); setPresets(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const moveOrder = async (order, direction) => {
    const idx = stages.findIndex((s) => s.id === order.current_stage_id);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= stages.length) return;
    await api.post(`/orders/${order.id}/move`, { stage_id: stages[newIdx].id });
    load();
  };
  const complete = async (o) => {
    try {
      const { data } = await api.post(`/orders/${o.id}/complete`);
      const profit = Number(data?.profit || 0);
      const short = data?.shortages || [];
      toast.success(`Tamamlandı · Kâr $${profit.toFixed(2)}`);
      if (short.length) toast.warning(`Stok yetersiz: ${short.map((s) => s.name).join(", ")}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const openPaint = (o) => {
    setPaintOrder(o);
    setPaintHex(o.paint_color?.hex || "#000000");
    setPaintLabel(o.paint_color?.label || "");
  };
  const savePaint = async () => {
    try {
      await api.post(`/orders/${paintOrder.id}/paint-color`, { hex: paintHex, label: paintLabel });
      toast.success("Boya rengi kaydedildi");
      setPaintOrder(null); load();
    } catch (e) { toast.error("Hata"); }
  };
  const clearPaint = async () => {
    await api.delete(`/orders/${paintOrder.id}/paint-color`);
    toast.success("Boya rengi silindi");
    setPaintOrder(null); load();
  };

  const openTrack = (o) => {
    setTrackOrder(o);
    setTrackCode(o.tracking?.code || "");
    setTrackCarrier(o.tracking?.carrier || "");
  };
  const sendTrack = async () => {
    if (!trackCode || !trackCarrier) return toast.error("Kod ve kargo firması zorunlu");
    try {
      const { data } = await api.post(`/orders/${trackOrder.id}/etsy-tracking`, { tracking_code: trackCode, carrier_name: trackCarrier, send_bcc: true });
      if (data.ok) toast.success("Etsy'ye takip bilgisi gönderildi ve alıcıya bildirim yollandı");
      else toast.error(`Etsy hatası: ${data.detail || data.status}`);
      setTrackOrder(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  return (
    <div className="p-8">
      <PageHeader eyebrow="Kanban" title="Üretim Panosu" subtitle="Aktif siparişleri aşamalar arasında ilerletin — dokunmatik ekran için optimize" />
      <div className="flex gap-4 overflow-x-auto scroll-slim pb-4">
        {stages.map((s) => {
          const cards = orders.filter((o) => o.current_stage_id === s.id);
          const isPaintStage = s.name.toLowerCase().includes("boya");
          const isShipStage = s.name.toLowerCase().includes("kargo");
          return (
            <div key={s.id} className="kanban-col bg-slate-100/70 rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: s.color }} />
                  <h3 className="font-display font-bold text-slate-800 text-base">{s.name}</h3>
                </div>
                <span className="text-xs uppercase tracking-widest font-bold text-slate-500 bg-white border border-slate-200 rounded-md px-2 py-0.5">
                  {cards.length}
                </span>
              </div>
              <div className="space-y-3 min-h-[200px]">
                {cards.map((o) => {
                  const first = (o.items || [])[0];
                  const hasPaint = !!o.paint_color?.hex;
                  const isEtsy = o.source === "etsy";
                  return (
                    <Card key={o.id} className="p-3 border-slate-200 shadow-sm bg-white stagger-enter">
                      <div className="flex items-start gap-3 mb-2">
                        <Thumb src={first?.image_url} alt={first?.product_name} />
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-[11px] font-semibold text-slate-500 truncate">{o.order_no}</div>
                          <div className="font-semibold text-slate-900 text-sm mt-0.5 truncate">{o.customer?.name || "-"}</div>
                          <div className="text-xs font-bold text-slate-700 mt-0.5">{currency(o.total)}</div>
                        </div>
                      </div>

                      {/* Ürünler: küçük başlık + BÜYÜK varyasyon */}
                      <div className="space-y-2 mb-2">
                        {o.items?.map((i, idx) => (
                          <div key={`${o.id}-i-${idx}`} className="border border-slate-200 rounded-lg p-2 bg-white">
                            <div className="text-[10px] text-slate-500 truncate mb-1">{i.qty}× {i.etsy_title || i.product_name}</div>
                            {(i.variations && i.variations.length > 0) ? (
                              <div className="flex flex-wrap gap-1.5">
                                {i.variations.map((v, vi) => (
                                  <span key={vi} className="inline-flex flex-col items-start bg-slate-50 border border-slate-300 rounded-md px-2 py-1">
                                    <span className="text-[9px] uppercase tracking-widest font-bold text-slate-500 leading-none">{v.name}</span>
                                    <span className="text-lg font-extrabold text-slate-900 leading-tight">{v.value}</span>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-[10px] text-amber-600 font-semibold">Varyasyon yok — Ayarlar &gt; Etsy ile Eşitle çalıştırın</div>
                            )}
                            {i.personalization && (
                              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                                <div className="text-[9px] uppercase tracking-widest font-bold text-amber-700">Kişiselleştirme</div>
                                <div className="text-sm font-bold text-amber-900 whitespace-pre-wrap">{i.personalization}</div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {(hasPaint || isPaintStage) && (
                        <button
                          onClick={() => openPaint(o)}
                          className={`w-full flex items-center gap-2 rounded-lg border px-2.5 py-2 mb-2 transition-colors ${
                            hasPaint ? "bg-white border-slate-200 hover:bg-slate-50" : "border-dashed border-pink-300 bg-pink-50 hover:bg-pink-100"
                          }`}
                        >
                          {hasPaint ? (
                            <>
                              <span className="h-6 w-6 rounded-md border border-slate-300 shrink-0" style={{ backgroundColor: o.paint_color.hex }} />
                              <div className="text-left min-w-0 flex-1">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Boya Rengi</div>
                                <div className="text-sm font-bold text-slate-900 truncate">{o.paint_color.label || o.paint_color.hex}</div>
                              </div>
                            </>
                          ) : (
                            <>
                              <Palette className="h-4 w-4 text-pink-600" />
                              <span className="text-sm font-semibold text-pink-700">Boya rengi belirle</span>
                            </>
                          )}
                        </button>
                      )}

                      <div className="text-xs text-slate-600 line-clamp-2 mb-3">
                        {o.items?.map((i) => `${i.qty}× ${i.product_name}`).length > 3 && "..."}
                      </div>

                      {isShipStage && isEtsy && (
                        <button
                          onClick={() => openTrack(o)}
                          className={`w-full flex items-center gap-2 rounded-lg border px-2.5 py-2 mb-2 transition-colors ${
                            o.tracking ? "bg-emerald-50 border-emerald-200 hover:bg-emerald-100" : "border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100"
                          }`}
                        >
                          <Truck className={`h-4 w-4 ${o.tracking ? "text-emerald-600" : "text-blue-600"}`} />
                          <div className="text-left min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                              {o.tracking ? "Kargo Gönderildi" : "Etsy'ye Kargo Bilgisi"}
                            </div>
                            <div className="text-xs font-semibold text-slate-800 truncate">
                              {o.tracking ? `${o.tracking.carrier} · ${o.tracking.code}` : "Takip kodunu gir"}
                            </div>
                          </div>
                        </button>
                      )}

                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[10px] text-slate-400 shrink-0">
                          {dateShort(o.created_at)}{isEtsy && <span className="ml-1 text-orange-500 font-bold">· ETSY</span>}
                        </div>
                        <div className="flex gap-1.5">
                          <button
                            data-testid="board-prev-btn"
                            aria-label="Önceki aşama"
                            onClick={() => moveOrder(o, -1)}
                            className="h-12 w-12 rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 border border-slate-200 grid place-items-center transition-colors touch-manipulation">
                            <ChevronLeft className="h-6 w-6 text-slate-700" />
                          </button>
                          <button
                            data-testid="board-next-btn"
                            aria-label="Sonraki aşama"
                            onClick={() => moveOrder(o, 1)}
                            className="h-12 w-12 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 grid place-items-center transition-colors touch-manipulation">
                            <ChevronRight className="h-6 w-6 text-white" />
                          </button>
                          <button
                            data-testid="board-complete-btn"
                            aria-label="Tamamla"
                            onClick={() => complete(o)}
                            className="h-12 w-12 rounded-lg bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 grid place-items-center transition-colors touch-manipulation">
                            <CheckCircle2 className="h-6 w-6 text-white" />
                          </button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
                {cards.length === 0 && <div className="text-xs text-slate-400 text-center py-6">Boş</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!paintOrder} onOpenChange={(v) => !v && setPaintOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Boya Rengi · {paintOrder?.order_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-24 w-24 rounded-xl border-2 border-slate-200 shadow-inner" style={{ backgroundColor: paintHex }} />
              <div className="flex-1 space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold text-slate-600">Renk</Label>
                  <input type="color" value={paintHex} onChange={(e) => setPaintHex(e.target.value)} className="mt-1 h-12 w-full rounded-lg cursor-pointer border border-slate-200" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-600">Etiket / Kod</Label>
              <Input value={paintLabel} onChange={(e) => setPaintLabel(e.target.value)} placeholder="Örn. Mat siyah, RAL 9005..." className="text-base h-12" />
            </div>
            <div className="text-xs text-slate-500 flex flex-wrap gap-1.5">
              {presets.length === 0 && <span className="text-slate-400">Ayarlar &gt; Boya Renkleri'nden hazır renk ekleyin</span>}
              {presets.map((p) => (
                <button key={p.id} type="button" onClick={() => { setPaintHex(p.hex); setPaintLabel(p.label); }}
                  className="flex items-center gap-1.5 border border-slate-200 rounded-full px-2 py-1 hover:bg-slate-50">
                  <span className="h-3 w-3 rounded-full border border-slate-300" style={{ backgroundColor: p.hex }} />
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2">
            {paintOrder?.paint_color && (
              <Button variant="ghost" onClick={clearPaint} className="text-red-600 hover:text-red-700 h-11">
                <X className="h-4 w-4 mr-1" /> Sil
              </Button>
            )}
            <Button onClick={savePaint} className="bg-blue-600 hover:bg-blue-700 h-11">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!trackOrder} onOpenChange={(v) => !v && setTrackOrder(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Etsy Kargo Bilgisi · {trackOrder?.order_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-600">Kargo Firması</Label>
              <Input value={trackCarrier} onChange={(e) => setTrackCarrier(e.target.value)} placeholder="Yurtiçi, Aras, MNG, PTT..." className="h-12 text-base" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-600">Takip Kodu</Label>
              <Input value={trackCode} onChange={(e) => setTrackCode(e.target.value)} placeholder="Kargo takip numarası" className="h-12 text-base" />
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
              Bu bilgi Etsy&apos;ye iletilir ve alıcıya otomatik bildirim gider.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={sendTrack} className="bg-blue-600 hover:bg-blue-700 h-11">Etsy&apos;ye Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
